import React from 'react';

// ─── Filter Section Interfaces & Component ──────────────────────────────────
export interface FilterConfig {
    value: string;
    onChange: (val: any) => void;
    options: { value: string; label: string }[];
    className?: string;
}

interface MasterFiltersProps {
    search?: string;
    onSearchChange?: (val: string) => void;
    searchPlaceholder?: string;
    searchWidthClass?: string;
    filters?: FilterConfig[];
    hasActiveFilters: boolean;
    onClearFilters: () => void;
}

export function MasterFilters({
    search,
    onSearchChange,
    searchPlaceholder = 'Search...',
    searchWidthClass = 'w-56',
    filters = [],
    hasActiveFilters,
    onClearFilters,
}: MasterFiltersProps) {
    return (
        <div className="flex flex-wrap items-center gap-3">
            {onSearchChange !== undefined && search !== undefined && (
                <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8] text-sm" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={searchPlaceholder}
                        className={`h-9 pl-9 pr-4 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 ${searchWidthClass}`}
                    />
                </div>
            )}
            {filters.map((filter, idx) => (
                <select
                    key={idx}
                    value={filter.value}
                    onChange={(e) => filter.onChange(e.target.value)}
                    className={`h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 cursor-pointer ${filter.className || ''
                        }`}
                >
                    {filter.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
            ))}
            {hasActiveFilters && (
                <button
                    onClick={onClearFilters}
                    className="text-xs text-[#4f46e5] hover:text-indigo-700 font-medium cursor-pointer whitespace-nowrap"
                >
                    Clear filters
                </button>
            )}
        </div>
    );
}

// ─── Summary / Breakdown Cards Interfaces & Component ───────────────────────
export interface SummaryCardItem {
    value: string;
    count: number;
    label: string;
    badgeClass?: string;
}

interface MasterSummaryCardsProps {
    items: SummaryCardItem[];
    activeFilterValue: string;
    onFilterChange: (val: any) => void;
    gridClassName?: string;
}

const GRID_COLS_MAP: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
    5: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
    6: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6',
    7: 'grid-cols-2 sm:grid-cols-4 lg:grid-cols-7',
};

export function MasterSummaryCards({
    items,
    activeFilterValue,
    onFilterChange,
    gridClassName,
}: MasterSummaryCardsProps) {
    const defaultGridClass = `grid ${GRID_COLS_MAP[items.length] || 'grid-cols-2 md:grid-cols-4'} gap-3`;
    const finalGridClass = gridClassName || defaultGridClass;

    return (
        <div className={finalGridClass}>
            {items.map((item) => {
                const isActive = activeFilterValue === item.value;
                const badgeClass = item.badgeClass || 'bg-slate-50 text-slate-700 border-slate-200';
                return (
                    <button
                        key={item.value}
                        onClick={() => onFilterChange(activeFilterValue === item.value ? 'ALL' : item.value)}
                        className={`bg-white border rounded-xl p-3 flex flex-col items-center gap-1.5 cursor-pointer transition-all ${isActive
                                ? `${badgeClass} border-current ring-2 ring-current/20`
                                : 'border-[#e2e8f0] hover:border-[#4f46e5]/30'
                            }`}
                    >
                        <p className="text-xl font-bold text-[#1e293b]">{item.count}</p>
                        <p className="text-[11px] text-[#64748b] font-medium text-center truncate w-full">{item.label}</p>
                    </button>
                );
            })}
        </div>
    );
}

// ─── Stats Row Interfaces & Component ────────────────────────────────────────
export interface StatItem {
    label: string;
    value: string | number;
    icon: string;
    bg: string;
    color: string;
}

interface MasterStatsRowProps {
    stats: StatItem[];
    gridClassName?: string;
    valueClassName?: string;
}

export function MasterStatsRow({
    stats,
    gridClassName = 'grid grid-cols-3 gap-4',
    valueClassName = 'text-2xl',
}: MasterStatsRowProps) {
    return (
        <div className={gridClassName}>
            {stats.map((c) => (
                <div key={c.label} className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${c.bg} shrink-0`}>
                        <i className={`${c.icon} ${c.color} text-lg`} />
                    </div>
                    <div>
                        <p className={`font-bold text-[#1e293b] ${valueClassName}`}>{c.value}</p>
                        <p className="text-xs text-[#64748b]">{c.label}</p>
                    </div>
                </div>
            ))}
        </div>
    );
}

// ─── Active / Switch Toggle Component ─────────────────────────────────────────
export interface MasterToggleProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    label?: string;
    activeLabel?: string;
    inactiveLabel?: string;
    description?: string;
    disabled?: boolean;
    className?: string;
}

export function MasterToggle({
    checked,
    onChange,
    label,
    activeLabel,
    inactiveLabel,
    description,
    disabled = false,
    className = '',
}: MasterToggleProps) {
    const textLabel = activeLabel && inactiveLabel
        ? (checked ? activeLabel : inactiveLabel)
        : label;

    return (
        <div className={`flex items-center gap-3 ${className}`}>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                disabled={disabled}
                onClick={() => !disabled && onChange(!checked)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30 disabled:opacity-50 disabled:cursor-not-allowed ${
                    checked ? 'bg-[#4f46e5]' : 'bg-slate-200'
                }`}
            >
                <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                        checked ? 'translate-x-5' : 'translate-x-0'
                    }`}
                />
            </button>
            {textLabel && (
                <label
                    onClick={() => !disabled && onChange(!checked)}
                    className="text-sm font-medium text-[#475569] cursor-pointer select-none"
                >
                    {textLabel}
                    {description && <span className="block text-xs text-[#94a3b8] font-normal">{description}</span>}
                </label>
            )}
        </div>
    );
}
